<template>
  <div :class="wrapperClasses">
    <!-- Label -->
    <label v-if="label" :for="id" :class="labelClasses">
      {{ label }}
      <span v-if="required" class="text-danger ml-0.5">*</span>
    </label>

    <!-- Select wrapper -->
    <div class="relative">
      <select
        :id="id"
        :value="modelValue"
        :disabled="disabled"
        :required="required"
        :class="selectClasses"
        @change="handleChange"
        @blur="handleBlur"
        @focus="handleFocus"
      >
        <!-- Placeholder option -->
        <option v-if="placeholder" value="" disabled selected>{{ placeholder }}</option>

        <!-- Options from array -->
        <option
          v-for="option in normalizedOptions"
          :key="option.value"
          :value="option.value"
          :disabled="option.disabled"
        >
          {{ option.label }}
        </option>
      </select>

      <!-- Dropdown icon -->
      <div class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
        <i class="fas fa-chevron-down text-gray-400 text-xs"></i>
      </div>
    </div>

    <!-- Helper text or error message -->
    <p v-if="error" class="mt-1 text-xs text-danger">{{ error }}</p>
    <p v-else-if="hint" class="mt-1 text-xs text-gray-500">{{ hint }}</p>
  </div>
</template>

<script>
export default {
  name: 'BaseSelect',
  props: {
    modelValue: {
      type: [String, Number, Boolean],
      default: ''
    },
    id: {
      type: String,
      default: () => `select-${Math.random().toString(36).substr(2, 9)}`
    },
    label: {
      type: String,
      default: null
    },
    placeholder: {
      type: String,
      default: null
    },
    options: {
      type: Array,
      required: true,
      validator: (value) => Array.isArray(value)
    },
    hint: {
      type: String,
      default: null
    },
    error: {
      type: String,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: 'md',
      validator: (value) => ['sm', 'md', 'lg'].includes(value)
    }
  },
  computed: {
    normalizedOptions() {
      return this.options.map(option => {
        if (typeof option === 'string') {
          return {
            label: option,
            value: option,
            disabled: false
          }
        }

        return {
          label: option.label || option.value,
          value: option.value,
          disabled: option.disabled || false
        }
      })
    },
    wrapperClasses() {
      return 'w-full'
    },
    labelClasses() {
      return 'block mb-1.5 text-sm font-medium text-gray-700'
    },
    selectClasses() {
      return [
        'block w-full pr-10 border rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary appearance-none',
        this.sizeClasses,
        this.error ? 'border-danger focus:ring-red-500/20 focus:border-danger' : 'border-gray-300',
        this.disabled ? 'bg-gray-50 cursor-not-allowed opacity-60' : 'bg-white cursor-pointer'
      ]
    },
    sizeClasses() {
      const sizes = {
        sm: 'px-3 py-1.5 text-sm',
        md: 'px-3 py-2 text-sm',
        lg: 'px-4 py-2.5 text-base'
      }
      return sizes[this.size]
    }
  },
  methods: {
    handleChange(event) {
      let value = event.target.value

      const originalOption = this.normalizedOptions.find(opt => opt.value.toString() === value)
      if (originalOption && typeof originalOption.value === 'number') {
        value = Number(value)
      }

      this.$emit('update:modelValue', value)
      this.$emit('change', value)
    },
    handleBlur(event) {
      this.$emit('blur', event)
    },
    handleFocus(event) {
      this.$emit('focus', event)
    }
  }
}
</script>
